import React from 'react';
import moment from 'moment';
import { KeyboardDatePicker, MuiPickersUtilsProvider, KeyboardDateTimePicker } from '@material-ui/pickers';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import MomentUtils from '@date-io/moment';
import Typography from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Typography';
import { withStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import { fullDate } from 'Consts/DateFormats';
import { createBaseStyles } from 'Components/UI/styles';
import useMobileLayout from 'Hooks/useMobileLayout';

const styles = (theme) => ({
  root: {
    padding: theme.spacing(4),
    minWidth: '100%',
    backgroundColor: theme.palette.common.white,
    marginBottom: theme.spacing(2),
  },
  title: {
    fontWeight: theme.typography.fontWeightBold,
    fontSize: '1.2em',
  },
  schedule: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: theme.spacing(3),
    alignItems: 'flex-start',
  },
  scheduleMobile: {
    flexWrap: 'wrap',
  },
  dateTimePicker: {
    flexBasis: '23%',
  },
  dateTimePickerMobile: {
    flexBasis: '48%',
    marginTop: theme.spacing(1),
  },
  dateField: {
    display: 'none',
  },
  datesSelectedMessage: {},
  rootKeyboardDateTimePicker: {
    ...createBaseStyles(theme),
    width: '100%',
    paddingBottom: 0,
    '& .MuiFormHelperText-root': {
      position: 'relative',
    },
  },
});

const OutOfOfficeDateRangePicker = ({
  className,
  classes,
  today,
  scheduledDates,
  setScheduledDates,
  scheduledTimes,
  setScheduledTimes,
  outOfOfficeEnabled,
}) => {
  const isMobile = useMobileLayout();

  const handleDateChange = (newDateTime, type) => {
    if (newDateTime === null) {
      return;
    }
    newDateTime.set({
      hour: scheduledTimes[type].get('hour'),
      minute: scheduledTimes[type].get('minute'),
      second: scheduledTimes[type].get('second'),
    });
    // Check to see if date is before the current time.
    if (newDateTime.isBefore(today)) {
      const newTime = newDateTime.clone().add(-1, 'day');
      setScheduledTimes({
        ...scheduledTimes,
        [type]: newTime,
      });
      setScheduledDates({
        ...scheduledDates,
        [type]: newDateTime,
      });
      return;
    }
    // Only set the time if the date is valid
    if (newDateTime.isValid()) {
      setScheduledTimes({
        ...scheduledTimes,
        [type]: newDateTime,
      });
    }
    setScheduledDates({
      ...scheduledDates,
      [type]: newDateTime,
    });
  };

  const handleTimeChange = (newDateTime, type) => {
    if (newDateTime === null) return;

    newDateTime.set({
      year: scheduledDates[type].get('year'),
      month: scheduledDates[type].get('month'),
      date: scheduledDates[type].get('date'),
    });

    if (newDateTime.isBefore(moment())) {
      newDateTime.add(-1, 'day');
      setScheduledTimes({
        ...scheduledTimes,
        [type]: newDateTime,
      });
      return;
    }
    // Only set the the date if date is valid
    if (newDateTime.isValid()) {
      setScheduledDates({
        ...scheduledDates,
        [type]: newDateTime,
      });
    }
    setScheduledTimes({
      ...scheduledTimes,
      [type]: newDateTime,
    });
  };

  return (
    <MuiPickersUtilsProvider utils={MomentUtils}>
      <div
        className={classNames(classes.root, className)}
      >
        <Typography
          className={classes.title}
          color="primary"
          align="left"
          variant="h2"
        >
          {'Schedule'}
        </Typography>
        <div
          className={classNames(classes.schedule, {
            [classes.scheduleMobile]: isMobile,
          })}
        >
          {['start', 'end'].map(scheduleType => {
            return (
              <>
                <div
                  className={classNames({
                    [classes.dateTimePicker]: !isMobile,
                    [classes.dateTimePickerMobile]: isMobile,
                  })}
                >
                  <Typography
                    className={classes.subTitle}
                    color={'primary'}
                  >
                    {scheduleType === 'start' ? 'Leaving' : 'Returning'}
                  </Typography>
                  <KeyboardDatePicker
                    autoOk
                    disableToolbar
                    disablePast
                    disabled={outOfOfficeEnabled || (scheduleType === 'end' && !scheduledDates.start)}
                    orientation="landscape"
                    openTo="date"
                    format={fullDate}
                    minDate={scheduleType === 'end' && scheduledDates.start}
                    value={scheduledDates[scheduleType]}
                    onChange={date => handleDateChange(date, scheduleType)}
                    className={classes.rootKeyboardDateTimePicker}
                    classes={{
                      root: classes.rootKeyboardDateTimePicker,
                    }}
                    minDateMessage={scheduleType === 'end' ? 'Return date can not be before your departure.' : 'Start date can not be before today.'}
                    test={`out-of-office-${scheduleType}-date-input`}
                  />
                </div>
                <div
                  className={classNames({
                    [classes.dateTimePicker]: !isMobile,
                    [classes.dateTimePickerMobile]: isMobile,
                  })}
                >
                  <Typography
                    className={classes.subTitle}
                    color={'primary'}
                  >
                    {'Time (optional)'}
                  </Typography>
                  <KeyboardDateTimePicker
                    className={classes.rootKeyboardDateTimePicker}
                    classes={{
                      root: classes.rootKeyboardDateTimePicker,
                    }}
                    disablePast
                    disabled={!scheduledDates[scheduleType] || outOfOfficeEnabled}
                    value={scheduledTimes[scheduleType]}
                    onChange={date => handleTimeChange(date, scheduleType)}
                    format="hh:mm A"
                    KeyboardButtonProps={{ className: classes.dateField }}
                  />
                </div>
              </>
            );
          })}
        </div>
      </div>
    </MuiPickersUtilsProvider>
  );
};

OutOfOfficeDateRangePicker.defaultProps = {
  className: '',
  today: moment().startOf('day'),
};

OutOfOfficeDateRangePicker.propTypes = {
  className: PropTypes.string,
  classes: PropTypes.object.isRequired,
  scheduledDates: PropTypes.object.isRequired,
  setScheduledDates: PropTypes.func.isRequired,
  scheduledTimes: PropTypes.object.isRequired,
  setScheduledTimes: PropTypes.func.isRequired,
  today: PropTypes.object,
  outOfOfficeEnabled: PropTypes.bool.isRequired,
};

export default withStyles(styles)(OutOfOfficeDateRangePicker);
